import { twJoin } from "tailwind-merge";

import type { ImageDto } from "@/api/types";
import { FileAttachment01Icon } from "@/components/Icons/FileAttachment01Icon";
import { MediaXButton } from "@/components/Media/MediaXButton";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { formatFileSize } from "@/helpers/file-size";
import { useTranslation } from "@/translations";
import { RuntimeBackgroundImage } from "@/ui-adapters/RuntimeBackgroundImage";

interface PdfProps {
  previewImage?: ImageDto;
  fileName?: string;
  fileSize?: number;
  onClick?: () => void;
  onDelete?: () => void;
  isInvalid?: boolean;
}
export function Pdf({ previewImage, fileName, fileSize, isInvalid, onClick, onDelete }: PdfProps): React.ReactNode {
  const { t } = useTranslation();
  const Component = onClick ? "button" : "div";

  return (
    <Inline align="center" wrap={false}>
      <Component
        className={twJoin(
          "flex h-media-input items-center gap-space-sm rounded-lg border bg-background-secondary p-space-md text-left transition-colors",
          isInvalid
            ? "border-border-danger! focus-within:ring-4 focus-within:ring-border-danger-tertiary"
            : "border-transparent",
          onClick ? "cursor-pointer hocus:bg-background-tertiary/50" : undefined,
        )}
        type={onClick ? "button" : undefined}
        {...{
          onClick,
        }}
      >
        {previewImage ? (
          <RuntimeBackgroundImage imageUrl={previewImage.url} presentation="attachmentIcon" />
        ) : (
          <span className="flex justify-center text-text-primary">
            <FileAttachment01Icon size={32} />
          </span>
        )}
        <span className="w-full">
          <Stack as="span" gap="xs" align="start">
            <Text variant="body-emphasis" measure="compact" overflow="line-clamp-one">
              {fileName || "..."}
            </Text>
            {fileSize ? (
              <Text as="span" variant="caption" tone="secondary">
                {formatFileSize(fileSize, 0)}
                {fileName?.toLowerCase().endsWith("pdf") ? " PDF" : ""}
              </Text>
            ) : null}
          </Stack>
        </span>
      </Component>
      {onDelete && (
        <span className="ml-space-sm shrink-0">
          <MediaXButton onClick={onDelete} title={t("common.action.delete")} />
        </span>
      )}
    </Inline>
  );
}
